import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Coins, Rocket, RotateCw } from "lucide-react";
import { useApp } from "../context/AppContext";
import { gamesApi } from "../lib/api";
import { inr } from "../lib/format";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { useToast } from "../hooks/use-toast";
import { WinReveal } from "../components/WinReveal";
import { playCoinSplash, unlockAudio } from "../lib/rouletteSound";

const EDGE = 0.25;
const TARGET_CHIPS = [1.5, 2, 3, 5, 10, 25, 50, 100];

// Risk-based colours for the target chips — low & safe (green) → high & risky (red).
const targetColor = (t) => {
  if (t <= 2) return { grad: "linear-gradient(135deg,#34d399,#059669)", glow: "16,185,129", text: "#fff" };
  if (t <= 5) return { grad: "linear-gradient(135deg,#bef264,#84cc16)", glow: "132,204,22", text: "#152505" };
  if (t <= 25) return { grad: "linear-gradient(135deg,#fbbf24,#f59e0b)", glow: "245,158,11", text: "#1a1205" };
  if (t <= 50) return { grad: "linear-gradient(135deg,#fb923c,#ea580c)", glow: "249,115,22", text: "#fff" };
  return { grad: "linear-gradient(135deg,#fb7185,#e11d48)", glow: "244,63,94", text: "#fff" };
};

function playWhoosh() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext; if (!Ctx) return;
    const ctx = new Ctx(); const o = ctx.createOscillator(), g = ctx.createGain();
    o.type = "sawtooth"; o.frequency.setValueAtTime(200, ctx.currentTime);
    o.frequency.exponentialRampToValueAtTime(900, ctx.currentTime + 0.35);
    g.gain.setValueAtTime(0.0001, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.12, ctx.currentTime + 0.05);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.4);
    o.connect(g); g.connect(ctx.destination); o.start(); o.stop(ctx.currentTime + 0.42);
    setTimeout(() => ctx.close().catch(() => {}), 600);
  } catch { /* audio blocked */ }
}

function playBust() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext; if (!Ctx) return;
    const ctx = new Ctx(); const o = ctx.createOscillator(), g = ctx.createGain();
    o.type = "square"; o.frequency.setValueAtTime(300, ctx.currentTime);
    o.frequency.exponentialRampToValueAtTime(90, ctx.currentTime + 0.25);
    g.gain.setValueAtTime(0.16, ctx.currentTime); g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.3);
    o.connect(g); g.connect(ctx.destination); o.start(); o.stop(ctx.currentTime + 0.32);
    setTimeout(() => ctx.close().catch(() => {}), 500);
  } catch { /* audio blocked */ }
}

export default function Limbo() {
  const { isAuthed, balance, refresh } = useApp();
  const { toast } = useToast();
  const [stake, setStake] = useState("100");
  const [target, setTarget] = useState("2.00");
  const [rolling, setRolling] = useState(false);
  const [display, setDisplay] = useState(1.0);   // animated number on the board
  const [result, setResult] = useState(null);     // final rolled multiplier
  const [won, setWon] = useState(null);           // null | true | false
  const [reveal, setReveal] = useState(null);
  const [recent, setRecent] = useState([]);
  const streak = useRef(0);
  const rafRef = useRef(null);

  if (!isAuthed) return (
    <div className="max-w-lg mx-auto px-4 py-24 text-center" data-testid="limbo-signin-gate">
      <h2 className="font-display font-bold text-2xl">Sign in to play</h2>
      <Link to="/"><Button className="mt-6 font-semibold" data-testid="limbo-go-home">Go Home</Button></Link>
    </div>
  );

  const tgt = Number(target);
  const validTarget = tgt >= 1.01 && tgt <= 100;
  const winChance = validTarget ? Math.max(0, Math.min(100, (1 - EDGE) / tgt * 100)) : 0;

  const animateTo = (final) => {
    const start = performance.now();
    const dur = 850;
    const from = 1.0;
    const step = (t) => {
      const p = Math.min(1, (t - start) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      const val = from + (final - from) * eased;
      setDisplay(val);
      if (p < 1) rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
  };

  const play = async () => {
    const s = Number(stake);
    if (!s || s < 20) { toast({ title: "Minimum bet is ₹20" }); return; }
    if (s > 100000) { toast({ title: "Higher bet limit crossed", description: "Please bet ₹100000 or below." }); return; }
    if (s > balance) { toast({ title: "Insufficient balance" }); return; }
    if (!validTarget) { toast({ title: "Target must be between 1.01x and 100x" }); return; }
    unlockAudio(); playWhoosh();
    setRolling(true); setWon(null); setResult(null); setDisplay(1.0);
    try {
      const res = await gamesApi.limboPlay({ stake: s, target: Number(tgt.toFixed(2)) });
      animateTo(res.result);
      await new Promise((ok) => setTimeout(ok, 880));
      setResult(res.result);
      setWon(res.win);
      streak.current = res.win ? streak.current + 1 : 0;
      setRecent((p) => [{ m: res.result, won: res.win }, ...p].slice(0, 14));
      if (res.win) { playCoinSplash(); setReveal({ label: `${tgt.toFixed(2)}x`, won: true, payout: res.payout, streak: streak.current }); }
      else { playBust(); }
      await refresh();
    } catch (e) {
      toast({ title: "Error", description: e?.response?.data?.detail || "Try again" });
    } finally {
      setRolling(false);
    }
  };

  const boardColor = won === true ? "#34d399" : won === false ? "#f87171" : "#fde047";
  const boardGlow = won === true ? "52,211,153" : won === false ? "248,113,113" : "253,224,71";

  return (
    <div className="max-w-lg mx-auto px-4 sm:px-6 py-8 pb-28" data-testid="limbo-page">
      <WinReveal open={!!reveal} {...(reveal || {})} onClose={() => setReveal(null)} />
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display font-bold text-3xl flex items-center gap-2"><Rocket className="h-6 w-6 text-primary" /> Limbo</h1>
          <p className="text-sm text-muted-foreground">Set a target · beat the rocket · win big up to 100x</p>
        </div>
        <span className="rounded-full bg-secondary px-3 py-1.5 text-sm font-semibold" data-testid="limbo-balance">
          <Coins className="h-4 w-4 inline mr-1 text-primary" />{inr(balance)}
        </span>
      </div>

      {/* Result board */}
      <div className="relative rounded-2xl p-6 overflow-hidden"
        style={{
          background: "radial-gradient(ellipse 120% 90% at 50% 0%, #12203a 0%, #0a1120 60%, #05070d 100%)",
          border: "1px solid rgba(217,181,69,0.55)",
          boxShadow: "inset 0 0 0 3px rgba(217,181,69,0.22), inset 0 0 70px rgba(0,0,0,0.55), 0 10px 30px rgba(0,0,0,0.5)",
        }}>
        <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse 60% 40% at 50% 10%, rgba(255,255,255,0.08), transparent 60%)" }} />
        <div className="relative flex items-center justify-between mb-3">
          <div className="rounded-xl bg-black/30 px-3 py-1.5 ring-1 ring-white/10">
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground block leading-none mb-0.5">Target</span>
            <span className="font-display font-black text-lg text-primary" data-testid="limbo-target-readout">{validTarget ? `${tgt.toFixed(2)}x` : "—"}</span>
          </div>
          <div className="rounded-xl bg-black/30 px-3 py-1.5 ring-1 ring-white/10 text-right">
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground block leading-none mb-0.5">Win chance</span>
            <span className="font-display font-black text-lg text-emerald-300" data-testid="limbo-winchance">{winChance.toFixed(2)}%</span>
          </div>
        </div>

        <div className="relative grid place-items-center h-40 sm:h-48">
          <div className="text-center transition-transform duration-200" style={{ transform: rolling ? "scale(1.04)" : "scale(1)" }}>
            <span className="font-display font-black text-6xl sm:text-7xl tabular-nums"
              data-testid="limbo-result"
              style={{ color: boardColor, textShadow: `0 0 28px rgba(${boardGlow},0.65)` }}>
              {(result != null ? result : display).toFixed(2)}x
            </span>
            <div className="mt-2 h-6 text-sm">
              {won === true && <span className="font-semibold text-emerald-300" data-testid="limbo-status-won">🚀 You beat the target!</span>}
              {won === false && <span className="font-semibold text-red-400" data-testid="limbo-status-lost">Busted below {tgt.toFixed(2)}x</span>}
              {won === null && !rolling && <span className="text-muted-foreground">Set your target &amp; stake, then Launch</span>}
              {rolling && <span className="text-emerald-100/70">Launching…</span>}
            </div>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="mt-6 rounded-2xl border border-border bg-card p-6" data-testid="limbo-controls">
        <label className="text-sm font-medium">Target multiplier <span className="text-xs text-muted-foreground font-normal">(1.01x – 100x)</span></label>
        <Input type="number" min={1.01} max={100} step={0.01} value={target} disabled={rolling}
          onChange={(e) => setTarget(e.target.value)} className="mt-1.5 disabled:opacity-60" data-testid="limbo-target-input" />
        <div className="mt-2 flex flex-wrap gap-1.5">
          {TARGET_CHIPS.map((t) => {
            const c = targetColor(t);
            const on = Number(tgt.toFixed(2)) === Number(t.toFixed(2));
            return (
              <button key={t} onClick={() => !rolling && setTarget(t.toFixed(2))} disabled={rolling} data-testid={`limbo-target-chip-${t}`}
                style={on
                  ? { background: c.grad, color: c.text, boxShadow: `0 0 14px rgba(${c.glow},0.5), inset 0 1px 0 rgba(255,255,255,0.35)`, borderColor: "transparent" }
                  : { color: `rgb(${c.glow})`, borderColor: `rgba(${c.glow},0.45)` }}
                className={`rounded-full px-3.5 py-1 text-xs font-extrabold border transition-[filter,transform,opacity] disabled:opacity-40 ${
                  on ? "scale-105" : "bg-secondary/50 hover:bg-secondary"
                }`}>
                {t}x
              </button>
            );
          })}
        </div>

        <label className="mt-5 block text-sm font-medium">Stake <span className="text-xs text-muted-foreground font-normal">(min ₹20 · max ₹100,000)</span></label>
        <Input type="number" min={20} max={100000} value={stake} disabled={rolling}
          onChange={(e) => setStake(e.target.value)} className="mt-1.5 disabled:opacity-60" data-testid="limbo-stake-input" />
        <div className="mt-2 flex flex-wrap gap-1.5">
          {[20, 50, 100, 500, 1000, 2000, 5000, 10000].map((q) => {
            const special = q === 10000;
            return (
              <button key={q} onClick={() => !rolling && setStake(String(q))} disabled={rolling} data-testid={`limbo-chip-${q}`}
                className={special
                  ? "rounded-full px-3.5 py-1.5 text-sm font-extrabold text-black shadow-md ring-1 ring-yellow-200/70 hover:brightness-110 disabled:opacity-40 transition-[filter,opacity]"
                  : "rounded-full bg-secondary px-3 py-1 text-xs font-semibold hover:bg-secondary/70 disabled:opacity-40"}
                style={special ? { background: "linear-gradient(135deg,#fde68a,#f59e0b 55%,#b45309)" } : undefined}>
                +{q}
              </button>
            );
          })}
        </div>

        <Button className="mt-5 w-full h-12 font-extrabold text-base" onClick={play} disabled={rolling} data-testid="limbo-play-btn">
          <Rocket className="h-5 w-5 mr-1.5" />{rolling ? "Launching…" : "Launch"}
        </Button>

        {recent.length > 0 && (
          <div className="mt-4 flex items-center gap-2 overflow-x-auto no-scrollbar" data-testid="limbo-recent">
            <RotateCw className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            {recent.map((r, i) => (
              <span key={i} className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-bold ${r.won ? "bg-emerald-500/20 text-emerald-300" : "bg-red-500/15 text-red-300"}`}>{Number(r.m).toFixed(2)}x</span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
